import {groupBy} from '@benchling/js-utils';
import React from 'react';

import {assert} from 'Asserts';
import StorableChip from 'inventory/chips/StorableChip';
import {
  AnalysisInputs,
  AnalysisSourcesOrigin,
  AnalysisSourceRow,
  DatasetType,
  DocumentInfo,
  getStepOrDocumentFromSource,
  isLabAutoExecutionDatasetInfo,
  isPlateDesignSource,
  PlateDatasetType,
  PlateDesignSource,
  PlateRecordSource,
  OfflineResultSchemaResponse,
  StepInfo,
  StepOrDocumentAnalysisSources,
  TableSourceWithSchema,
  TestOrderSource,
  PlateSource,
  ScratchPlateSource,
  isSentinelDocumentOrStep,
} from 'procedures/design/ProcedureMethodAnalysisTypes';
import {ProcedureParameterType} from 'procedures/ProcedureDomainTypes';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import DatasetChip from 'ui-library/chips/DatasetChip';
import {TABLE_ROW_BODY_HEIGHT} from 'ui-library/scrollable/ScrollableTableConstants';

import {
  CreatedAtCellContent,
  DatasetCellContent,
  DataSourcesWithIcons,
  StepsCellContent,
} from './DatasetRendering';
import {
  getIndividualPlateDatasetName,
  getParameterDatasetTypeFromParameterTable,
  getParameterTypeFromDatasetType,
  getPlateDesignSourceIdentifier,
  getPlateRecordSourceIdentifier,
  getPlateSourceIdentifier,
  getPlateSummaryDatasetName,
  ParameterDatasetType,
  STATIC_DATASET_NAMES,
} from './procedureMethodAnalysisUtils';

function getParameterDatasetTypesFromPlate(plate: PlateSource | ScratchPlateSource): Array<DatasetType> {
  const datasetTypes = [];
  if (plate.hasDataParameterConfirmations) {
    datasetTypes.push(DatasetType.DATA_PARAMETER);
  }
  if (plate.hasMaterialInputConfirmations) {
    datasetTypes.push(DatasetType.MATERIAL_INPUT);
  }
  return datasetTypes;
}

enum ParameterDatasetSourceType {
  PLATE = 'PLATE',
  // An unpromoted scratch plate: no real inventory id to render a chip for, so shown as a plain label.
  SCRATCH_PLATE = 'SCRATCH_PLATE',
  TABLE = 'TABLE',
}

interface IndividualPlateSourceInfo {
  plateName: string;
  stepOrDocument: DocumentInfo | StepInfo;
}

function buildIndividualPlateSourceRow(
  sourceId: string,
  plateDatasetType: PlateDatasetType,
  datasetType: DatasetType,
  sources: Array<IndividualPlateSourceInfo>,
  dataSources: JSX.Element
): AnalysisSourceRow {
  assert(sources.length > 0, 'Expected at least one source to build an individual plate row from', {
    sourceId,
  });
  const plateSourceId = getPlateSourceIdentifier(sourceId, plateDatasetType);
  return {
    id: plateSourceId,
    data: {
      dataset: (
        <DatasetCellContent
          datasetName={getIndividualPlateDatasetName(sources[0].plateName, plateDatasetType)}
          datasetId={plateSourceId}
        />
      ),
      dataSources,
      creationTime: <CreatedAtCellContent createdAt={null} />,
      steps: <StepsCellContent stepsOrDocuments={sources.map(({stepOrDocument}) => stepOrDocument)} />,
    },
    extra: {datasetType},
  };
}

function getIndividualPlateRows(
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>
): Array<AnalysisSourceRow> {
  // Ordinary/promoted plates, keyed by their real inventory plate id -- the same plate could be used in
  // multiple plate maps or lab auto runs, so group by plate ID and get list of all documents where each
  // plate is used. StepCellsContent component takes care of de-duping documents.
  const plateSourcesByPlateId = groupBy(
    availableSourcesPerStep.flatMap((stepSources) =>
      stepSources.analysisInputs.plates.map((plate) => ({
        plateId: plate.plateId,
        plateName: plate.plateName,
        stepOrDocument: getStepOrDocumentFromSource(stepSources),
      }))
    ),
    ({plateId}) => plateId
  );
  const ordinaryRows = Object.entries(plateSourcesByPlateId).flatMap(([plateId, sources]) => [
    buildIndividualPlateSourceRow(
      plateId,
      PlateDatasetType.POSITIONS,
      DatasetType.INDIVIDUAL_PLATE_POSITIONS,
      sources,
      <StorableChip id={plateId} size={ChipSize.MINI} isLink={false} />
    ),
    buildIndividualPlateSourceRow(
      plateId,
      PlateDatasetType.CONTENTS,
      DatasetType.INDIVIDUAL_PLATE_CONTENTS,
      sources,
      <StorableChip id={plateId} size={ChipSize.MINI} isLink={false} />
    ),
  ]);

  // Scratch plates, keyed by their own scratch plate id -- always unpromoted (see `ScratchPlateSource`),
  // so routed to the scratch-specific dataset types, distinct from ordinary
  // INDIVIDUAL_PLATE_POSITIONS/CONTENTS. A promoted scratch plate's positions/contents are already
  // covered by the ordinary row above for its real plate id instead.
  const scratchPlateSourcesByScratchPlateId = groupBy(
    availableSourcesPerStep.flatMap((stepSources) =>
      stepSources.analysisInputs.scratchPlates.map((plate) => ({
        scratchPlateId: plate.scratchPlateId,
        plateName: plate.plateName,
        isScratchPlateDesign: plate.isScratchPlateDesign,
        stepOrDocument: getStepOrDocumentFromSource(stepSources),
      }))
    ),
    ({scratchPlateId}) => scratchPlateId
  );
  const scratchRows = Object.entries(scratchPlateSourcesByScratchPlateId).flatMap(
    ([scratchPlateId, sources]) => {
      assert(sources.length > 0, 'Expected at least one source to build a scratch plate row from', {
        scratchPlateId,
      });
      const dataSources = (
        <DataSourcesWithIcons dataSources={[{name: sources[0].plateName}]} iconName="plate" />
      );
      const rows = [
        buildIndividualPlateSourceRow(
          scratchPlateId,
          PlateDatasetType.POSITIONS,
          DatasetType.SCRATCH_PLATE_POSITIONS,
          sources,
          dataSources
        ),
      ];
      // A scratch plate design has no content layer -- contents can't be defined on a design at all --
      // so its contents dataset would always be empty. Leave it out entirely rather than offer a
      // useless export option.
      if (!sources[0].isScratchPlateDesign) {
        rows.push(
          buildIndividualPlateSourceRow(
            scratchPlateId,
            PlateDatasetType.CONTENTS,
            DatasetType.SCRATCH_PLATE_CONTENTS,
            sources,
            dataSources
          )
        );
      }
      return rows;
    }
  );

  return [...ordinaryRows, ...scratchRows];
}

/**
 * A plate or scratch plate, normalized to the shape `getPlateSummaryRows`/`getSchemalessScratchPlateSummaryRow`
 * need to group and render ordinary and scratch sources together in one summary view.
 */
interface SchemaGroupablePlate {
  // The plate's real inventory id (for ordinary/promoted plates) or its own scratch plate id (for
  // unpromoted ones) -- see `isRealPlate` to distinguish which.
  displayId: string;
  plateName: string;
  plateSchemaId: string | null;
  plateSchemaName: string | null;
  // True for ordinary/promoted plates (chip-renderable via ChipList/StorableChip); false for unpromoted
  // scratch plates (rendered as plain labels, since they have no real inventory id).
  isRealPlate: boolean;
  stepOrDocument: DocumentInfo | StepInfo;
}

function plateSourceToSchemaGroupable(
  plate: PlateSource,
  stepOrDocument: DocumentInfo | StepInfo
): SchemaGroupablePlate {
  return {
    displayId: plate.plateId,
    plateName: plate.plateName,
    plateSchemaId: plate.plateSchemaId,
    plateSchemaName: plate.plateSchemaName,
    isRealPlate: true,
    stepOrDocument,
  };
}

// Scratch plate designs never join summary grouping -- they only ever get their own
// SCRATCH_PLATE_POSITIONS row, mirroring inventory's Plate Design.
function scratchPlateSourceToSchemaGroupable(
  plate: ScratchPlateSource,
  stepOrDocument: DocumentInfo | StepInfo
): SchemaGroupablePlate | null {
  if (plate.isScratchPlateDesign) {
    return null;
  }
  return {
    displayId: plate.scratchPlateId,
    plateName: plate.plateName,
    plateSchemaId: plate.plateSchemaId,
    plateSchemaName: plate.plateSchemaName,
    isRealPlate: false,
    stepOrDocument,
  };
}

function getSchemaGroupablePlates(
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>
): Array<SchemaGroupablePlate> {
  return availableSourcesPerStep.flatMap((stepSources) => {
    const stepOrDocument = getStepOrDocumentFromSource(stepSources);
    return [
      ...stepSources.analysisInputs.plates.map((plate) =>
        plateSourceToSchemaGroupable(plate, stepOrDocument)
      ),
      ...stepSources.analysisInputs.scratchPlates
        .map((plate) => scratchPlateSourceToSchemaGroupable(plate, stepOrDocument))
        .filter((plate): plate is SchemaGroupablePlate => plate != null),
    ];
  });
}

function getPlateSummaryRows(
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>
): Array<AnalysisSourceRow> {
  const plateIdsByPlateSchemaId = groupBy(
    getSchemaGroupablePlates(availableSourcesPerStep).filter(
      (plate): plate is SchemaGroupablePlate & {plateSchemaId: string; plateSchemaName: string} =>
        plate.plateSchemaId != null
    ),
    (plate) => plate.plateSchemaId
  );
  const plateSummaryDataPerRow = Object.entries(plateIdsByPlateSchemaId).map(([plateSchemaId, plates]) => {
    assert(plates.length > 0, 'Expected at least one plate to build a plate summary row from', {
      plateSchemaId,
    });
    return {
      schemaId: plateSchemaId,
      // All have the same schema, so can pick schema name from any of them
      schemaName: plates[0].plateSchemaName,
      plates,
    };
  });
  return plateSummaryDataPerRow.map((rowData) => {
    // De-duplicate real plates (ordinary plates and promoted scratch plates share this bucket, since
    // both have a real inventory plate id).
    const realPlateIds = [
      ...new Set(rowData.plates.filter((plate) => plate.isRealPlate).map((plate) => plate.displayId)),
    ];
    // Unpromoted scratch plates have no real inventory id, so they can't be rendered via ChipList/
    // StorableChip -- shown as plain labels instead.
    const unpromotedScratchSources = rowData.plates.filter((plate) => !plate.isRealPlate);
    const hasBothDataSourceKinds = realPlateIds.length > 0 && unpromotedScratchSources.length > 0;
    return {
      id: rowData.schemaId,
      data: {
        dataset: (
          <DatasetCellContent
            datasetName={getPlateSummaryDatasetName(rowData.schemaName)}
            datasetId={rowData.schemaId}
          />
        ),
        dataSources: (
          <div>
            {realPlateIds.length > 0 && (
              <ChipList
                chipSize={ChipSize.MINI}
                isVertical={false}
                itemIds={realPlateIds}
                maxChips={2}
                showChipsAsLinks={false}
              />
            )}
            {unpromotedScratchSources.length > 0 && (
              <DataSourcesWithIcons
                dataSources={unpromotedScratchSources.map((source) => ({
                  name: source.plateName,
                  stepName: source.stepOrDocument.name,
                }))}
                iconName="plate"
              />
            )}
          </div>
        ),
        creationTime: <CreatedAtCellContent createdAt={null} />,
        steps: <StepsCellContent stepsOrDocuments={rowData.plates.map((plate) => plate.stepOrDocument)} />,
        // If both real-plate chips and scratch-plate labels are being rendered (as two lines in the row),
        // increase the row height so it doesn't look squished
        height: hasBothDataSourceKinds ? TABLE_ROW_BODY_HEIGHT * 2 : undefined,
      },
      extra: {datasetType: DatasetType.PLATE_SUMMARY},
    };
  });
}

/** Returns the static "Plates summary: No schema" summary row, or null if there are none in scope. */
export function getSchemalessScratchPlateSummaryRow(
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>
): AnalysisSourceRow | null {
  const schemalessScratchPlateSources = getSchemaGroupablePlates(availableSourcesPerStep).filter(
    (plate) => plate.plateSchemaId == null && !plate.isRealPlate
  );

  if (schemalessScratchPlateSources.length === 0) {
    return null;
  }

  const datasetType = DatasetType.SCHEMALESS_SCRATCH_PLATE_SUMMARY;
  return {
    id: datasetType,
    data: {
      dataset: (
        <DatasetCellContent datasetName={STATIC_DATASET_NAMES[datasetType]!} datasetId={datasetType} />
      ),
      creationTime: <CreatedAtCellContent createdAt={null} />,
      dataSources: (
        <DataSourcesWithIcons
          dataSources={schemalessScratchPlateSources.map((source) => ({
            name: source.plateName,
            stepName: source.stepOrDocument.name,
          }))}
          iconName="plate"
        />
      ),
      steps: (
        <StepsCellContent
          stepsOrDocuments={schemalessScratchPlateSources.map((source) => source.stepOrDocument)}
        />
      ),
    },
    extra: {datasetType},
  };
}

export function getPlateRows(
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>
): Array<AnalysisSourceRow> {
  const individualPlateRows = getIndividualPlateRows(availableSourcesPerStep);
  const plateSummaryDatasetRows: Array<AnalysisSourceRow> = [];
  if (individualPlateRows.length > 0) {
    plateSummaryDatasetRows.push(...getPlateSummaryRows(availableSourcesPerStep));
  }
  // Grouped with the other plate summary rows (schema-having summaries come first, matching the schema
  // list a user would otherwise scan top-to-bottom), not with the individual per-plate detail rows below.
  const schemalessScratchPlateSummaryRow = getSchemalessScratchPlateSummaryRow(availableSourcesPerStep);
  if (schemalessScratchPlateSummaryRow != null) {
    plateSummaryDatasetRows.push(schemalessScratchPlateSummaryRow);
  }
  return plateSummaryDatasetRows.concat(individualPlateRows);
}

export function getPlateRecordAndDesignRows(
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>
): Array<AnalysisSourceRow> {
  const plateSourceDataPerRow = availableSourcesPerStep.flatMap((stepSources) =>
    [...stepSources.analysisInputs.plateRecords, ...stepSources.analysisInputs.plateDesigns].flatMap(
      (plateMap: PlateRecordSource | PlateDesignSource) => [
        {
          plateMapSourceId: isPlateDesignSource(plateMap)
            ? getPlateDesignSourceIdentifier(plateMap, PlateDatasetType.POSITIONS)
            : getPlateRecordSourceIdentifier(plateMap, PlateDatasetType.POSITIONS),
          plateMapName: plateMap.name,
          datasetName: getIndividualPlateDatasetName(plateMap.name, PlateDatasetType.POSITIONS),
          creationTime: null,
          stepOrDocument: getStepOrDocumentFromSource(stepSources),
          datasetType: isPlateDesignSource(plateMap)
            ? DatasetType.PLATE_DESIGN_POSITIONS
            : DatasetType.PLATE_RECORD_POSITIONS,
        },
        {
          plateMapSourceId: isPlateDesignSource(plateMap)
            ? getPlateDesignSourceIdentifier(plateMap, PlateDatasetType.CONTENTS)
            : getPlateRecordSourceIdentifier(plateMap, PlateDatasetType.CONTENTS),
          plateMapName: plateMap.name,
          datasetName: getIndividualPlateDatasetName(plateMap.name, PlateDatasetType.CONTENTS),
          creationTime: null,
          stepOrDocument: getStepOrDocumentFromSource(stepSources),
          datasetType: isPlateDesignSource(plateMap)
            ? DatasetType.PLATE_DESIGN_CONTENTS
            : DatasetType.PLATE_RECORD_CONTENTS,
        },
      ]
    )
  );

  return plateSourceDataPerRow.map((rowData) => ({
    id: rowData.plateMapSourceId,
    data: {
      dataset: <DatasetCellContent datasetName={rowData.datasetName} datasetId={rowData.plateMapSourceId} />,
      dataSources: (
        <DataSourcesWithIcons
          dataSources={[{name: rowData.plateMapName, stepName: rowData.stepOrDocument.name}]}
          iconName="plate"
        />
      ),
      creationTime: <CreatedAtCellContent createdAt={rowData.creationTime} />,
      steps: <StepsCellContent stepsOrDocuments={[rowData.stepOrDocument]} />,
    },
    extra: {datasetType: rowData.datasetType},
  }));
}

export function getSchemaSourceRows(
  sourcesPerStep: Array<StepOrDocumentAnalysisSources>,
  getTableSources: (analysisInputs: AnalysisInputs) => Array<TableSourceWithSchema>,
  datasetType: DatasetType,
  includeDataSourcesWithoutData: boolean
): Array<AnalysisSourceRow> {
  const tableSourcesWithDocumentInfo = sourcesPerStep.flatMap((stepSources) =>
    getTableSources(stepSources.analysisInputs).map((table) => ({
      schemaId: table.schemaId,
      schemaName: table.schemaName,
      tableName: table.tableName,
      hasCreationContext: table.hasCreationContext,
      stepOrDocument: getStepOrDocumentFromSource(stepSources),
    }))
  );
  const tableSourcesBySchemaId = groupBy(tableSourcesWithDocumentInfo, ({schemaId}) => schemaId);
  const dataPerRow = Object.entries(tableSourcesBySchemaId)
    .map(([schemaId, tableSources]) => ({
      schemaId,
      schemaName: tableSources[0].schemaName,
      tableSources: tableSources.filter(
        (tableSource) => includeDataSourcesWithoutData || tableSource.hasCreationContext
      ),
      creationTime: null,
    }))
    .filter((rowData) => rowData.tableSources.length > 0);
  return dataPerRow.map((rowData) => ({
    id: rowData.schemaId,
    data: {
      dataset: <DatasetCellContent datasetName={rowData.schemaName} datasetId={rowData.schemaId} />,
      creationTime: <CreatedAtCellContent createdAt={rowData.creationTime} />,
      dataSources: (
        <DataSourcesWithIcons
          dataSources={rowData.tableSources.map((tableSource) => ({
            name: tableSource.tableName,
            stepName: tableSource.stepOrDocument.name,
          }))}
          iconName="table"
        />
      ),
      steps: (
        <StepsCellContent stepsOrDocuments={rowData.tableSources.map(({stepOrDocument}) => stepOrDocument)} />
      ),
    },
    extra: {datasetType},
  }));
}

const OUTSIDE_OF_WORKSHEET_LABEL = 'Outside of worksheet';

export function getOfflineOnlyResultSchemaRows(
  offlineResultSchemas: Array<OfflineResultSchemaResponse>,
  inWorksheetSchemaIds: Set<string>
): Array<AnalysisSourceRow> {
  return offlineResultSchemas
    .filter((schema) => !inWorksheetSchemaIds.has(schema.schemaId))
    .map((schema) => ({
      id: schema.schemaId,
      data: {
        dataset: <DatasetCellContent datasetName={schema.schemaName} datasetId={schema.schemaId} />,
        dataSources: <span>{OUTSIDE_OF_WORKSHEET_LABEL}</span>,
        creationTime: <CreatedAtCellContent createdAt={null} />,
        steps: <span>N/A</span>,
      },
      extra: {datasetType: DatasetType.RESULT_SCHEMA},
    }));
}

export function getConditionsDatasetRow(): AnalysisSourceRow {
  return {
    id: DatasetType.CONDITIONS,
    data: {
      dataset: (
        <DatasetCellContent
          datasetName={STATIC_DATASET_NAMES[DatasetType.CONDITIONS]!}
          datasetId={DatasetType.CONDITIONS}
        />
      ),
      creationTime: <CreatedAtCellContent createdAt={null} />,
      // This dataset only contains condition-replicate information and method start and end times, so it has
      // no data sources within the worksheet
      dataSources: <span>N/A</span>,
      steps: <span>N/A</span>,
    },
    extra: {datasetType: DatasetType.CONDITIONS},
  };
}

/** Renders the test orders dataset row for the analysis source table.
 *
 * Dataset:
 * Test orders when the origin is configuration we can expect for there to be at most one test order
 * across all analysis sources since they are not associated with any specific step. When the origin is execution,
 * there will be a test order response for each document that has a test order. Regardless of how many test orders
 * responses there are, we group them all into one test order dataset row.
 *
 * Data sources:
 * If the analysis sources are from configuration, the data sources will be "Tests & Dilutions".
 * If the analysis sources are from execution, the data sources will be the plates that the test orders were confirmed in.
 * When there are no plates, the data sources will be "N/A".
 *
 * Steps:
 * If the analysis sources are from configuration, the steps will be "N/A".
 * If the analysis sources are from execution, the steps will be the steps that the test orders were confirmed in.
 *
 * @param analysisSourcesOrigin - The origin of the analysis sources (configuration or execution)
 * @param availableSourcesPerStep - The available sources per step
 * @param navigateToTests - The function to navigate to the tests page when the analysis sources are from configuration and
 * the user clicks on the tests & dilutions link
 * @returns The test order dataset row or null if there are no test orders
 */
export function getTestOrderDatasetRow(
  analysisSourcesOrigin: AnalysisSourcesOrigin,
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>,
  navigateToTests?: () => void
): AnalysisSourceRow | null {
  const testOrders = availableSourcesPerStep
    .map((stepSources) => stepSources.analysisInputs.testOrders)
    .filter((testOrder): testOrder is TestOrderSource => testOrder != null);

  if (testOrders.length === 0) {
    return null;
  }

  assert(
    analysisSourcesOrigin === AnalysisSourcesOrigin.EXECUTION || testOrders.length === 1,
    "Expected at most 1 test order to be present across all analysis sources, but found multiple. This isn't currently supported.",
    {testOrders}
  );

  const datasetType = DatasetType.TEST_ORDERS;
  const plateIds = testOrders.flatMap((testOrder) => testOrder.plateIds);
  let dataSourcesElement: JSX.Element = <span>N/A</span>;
  let steps: JSX.Element = <span>N/A</span>;

  if (analysisSourcesOrigin === AnalysisSourcesOrigin.CONFIGURATION) {
    dataSourcesElement = navigateToTests ? (
      <a onClick={navigateToTests}>Tests & Dilutions</a>
    ) : (
      <span>Tests & Dilutions</span>
    );
  }

  if (analysisSourcesOrigin === AnalysisSourcesOrigin.EXECUTION && plateIds.length > 0) {
    const stepsOrDocument = availableSourcesPerStep
      .filter((stepSources) => stepSources.analysisInputs.testOrders != null)
      // Test orders without a plate association will be in the sentinel document.
      // We don't want to display the sentinel document as a real document, so we need to filter it out.
      .filter((stepSources) => !isSentinelDocumentOrStep(stepSources))
      .map((stepSource) => getStepOrDocumentFromSource(stepSource));
    dataSourcesElement = (
      <ChipList
        chipSize={ChipSize.MINI}
        isVertical={false}
        itemIds={plateIds}
        maxChips={2}
        showChipsAsLinks={false}
      />
    );
    steps = <StepsCellContent stepsOrDocuments={stepsOrDocument} />;
  }

  return {
    id: DatasetType.TEST_ORDERS,
    data: {
      dataset: (
        <DatasetCellContent
          datasetName={STATIC_DATASET_NAMES[datasetType]!}
          datasetId={DatasetType.TEST_ORDERS}
        />
      ),
      creationTime: <CreatedAtCellContent createdAt={null} />,
      dataSources: dataSourcesElement,
      steps,
    },
    extra: {datasetType},
  };
}

/** Renders the previous test orders dataset row for the analysis source table.
 *
 * There is only one previous test order dataset that groups all previous test orders. This means that we can
 * only expect one previous test order response to be present across all analysis sources. If the analysis
 * sources are from configuration, the data sources will be "Informed by execution". If the analysis sources
 * are from execution, the data sources will be the studies the previous test orders are associated to.
 * If there are no studies, the data sources will be "N/A".
 */
export function getPreviousTestOrderDatasetRow(
  analysisSourcesOrigin: AnalysisSourcesOrigin,
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>
): AnalysisSourceRow | null {
  const previousTestOrders = availableSourcesPerStep
    .map((stepSources) => stepSources.analysisInputs.previousTestOrders)
    .filter((previousTestOrders) => previousTestOrders != null);

  if (previousTestOrders.length === 0) {
    return null;
  }

  assert(
    previousTestOrders.length === 1,
    "Expected at most 1 previous test order to be present across all analysis sources, but found multiple. This isn't currently supported.",
    {previousTestOrders}
  );

  const datasetType = DatasetType.PREVIOUS_TEST_ORDERS;
  const studies = previousTestOrders[0].studies;

  let dataSourcesElement: JSX.Element =
    analysisSourcesOrigin === AnalysisSourcesOrigin.CONFIGURATION ? (
      <span>Informed by execution</span>
    ) : (
      <span>N/A</span>
    );

  if (analysisSourcesOrigin === AnalysisSourcesOrigin.EXECUTION && studies.length > 0) {
    const studyDataSources = studies.map((studyInfo) => ({
      name: studyInfo.name,
      url: studyInfo.editUrl,
    }));
    dataSourcesElement =
      studyDataSources.length > 0 ? (
        <DataSourcesWithIcons
          dataSources={studyDataSources}
          iconName="study"
          onlyEnablePopoverOnOverflow={true}
        />
      ) : (
        dataSourcesElement
      );
  }

  return {
    id: DatasetType.PREVIOUS_TEST_ORDERS,
    data: {
      dataset: (
        <DatasetCellContent
          datasetName={STATIC_DATASET_NAMES[datasetType]!}
          datasetId={DatasetType.PREVIOUS_TEST_ORDERS}
        />
      ),
      creationTime: <CreatedAtCellContent createdAt={null} />,
      dataSources: dataSourcesElement,
      steps: <span>N/A</span>,
    },
    extra: {datasetType},
  };
}

export function getTestOrdersDatasetRows(
  analysisSourcesOrigin: AnalysisSourcesOrigin,
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>,
  navigateToTests?: () => void
): Array<AnalysisSourceRow> {
  const testOrderRow: AnalysisSourceRow | null = getTestOrderDatasetRow(
    analysisSourcesOrigin,
    availableSourcesPerStep,
    navigateToTests
  );
  const previousTestOrderRow: AnalysisSourceRow | null = getPreviousTestOrderDatasetRow(
    analysisSourcesOrigin,
    availableSourcesPerStep
  );

  return [testOrderRow, previousTestOrderRow].filter((row): row is AnalysisSourceRow => row != null);
}

export function getParameterDatasetRows(
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>,
  availableParameterTypes: Array<ProcedureParameterType>,
  includeDataSourcesWithoutData: boolean
): Array<AnalysisSourceRow> {
  const baseParameterTableSources = availableSourcesPerStep.flatMap((stepSources) =>
    stepSources.analysisInputs.parameterTables.map((parameterTable) => ({
      parameterDatasetType: getParameterDatasetTypeFromParameterTable(parameterTable.mode),
      tableName: parameterTable.tableName,
      hasCreationContext: parameterTable.hasCreationContext,
      stepOrDocument: getStepOrDocumentFromSource(stepSources),
    }))
  );
  // If necessary, filter out tables that would produce empty datasets
  const filteredParameterTableSources = baseParameterTableSources.filter(
    (parameterTableSource) =>
      includeDataSourcesWithoutData ||
      // Only the material output confirmation tables need to be filtered because all other parameter dataset types have planned values, which are always present
      parameterTableSource.parameterDatasetType !== DatasetType.MATERIAL_OUTPUT ||
      parameterTableSource.hasCreationContext
  );
  // Standardize to type consistent with plate source type
  const standardizedParameterTableSources = filteredParameterTableSources.map((parameterTableSource) => ({
    parameterDatasetType: parameterTableSource.parameterDatasetType,
    sourceInfo: {
      sourceIdentifier: parameterTableSource.tableName,
      sourceType: ParameterDatasetSourceType.TABLE,
      stepOrDocument: parameterTableSource.stepOrDocument,
    },
  }));

  const plateSources = availableSourcesPerStep.flatMap((stepSources) =>
    stepSources.analysisInputs.plates.flatMap((plate) =>
      getParameterDatasetTypesFromPlate(plate).map((parameterDatasetType) => ({
        parameterDatasetType,
        sourceInfo: {
          sourceIdentifier: plate.plateId,
          sourceType: ParameterDatasetSourceType.PLATE,
          stepOrDocument: getStepOrDocumentFromSource(stepSources),
        },
      }))
    )
  );
  // Unpromoted scratch plates confirm parameters against their own wells the same way real plates do; a
  // promoted one is already in `plates` above. Identified by name since there's no real plate to chip.
  const scratchPlateSources = availableSourcesPerStep.flatMap((stepSources) =>
    stepSources.analysisInputs.scratchPlates.flatMap((plate) =>
      getParameterDatasetTypesFromPlate(plate).map((parameterDatasetType) => ({
        parameterDatasetType,
        sourceInfo: {
          sourceIdentifier: plate.plateName,
          sourceType: ParameterDatasetSourceType.SCRATCH_PLATE,
          stepOrDocument: getStepOrDocumentFromSource(stepSources),
        },
      }))
    )
  );
  // Include these so table rows for parameter datasets that have parameter values but don't have any data sources (e.g. if table was deleted) are included
  const parameterDatasetTypes: Array<ParameterDatasetType> = [
    DatasetType.DATA_PARAMETER,
    DatasetType.MATERIAL_INPUT,
    DatasetType.MATERIAL_OUTPUT,
    DatasetType.EQUIPMENT,
  ];
  const rawParameterSources = parameterDatasetTypes
    .filter((parameterDatasetType) => {
      const parameterType = getParameterTypeFromDatasetType(parameterDatasetType);
      return availableParameterTypes.includes(parameterType);
    })
    .map((parameterDatasetType) => ({
      parameterDatasetType,
      sourceInfo: null,
    }));
  const sourcesByDatasetType = groupBy(
    [...standardizedParameterTableSources, ...plateSources, ...scratchPlateSources, ...rawParameterSources],
    ({parameterDatasetType}) => parameterDatasetType
  );

  const dataPerRow = Object.entries(sourcesByDatasetType).map(([parameterDatasetType, sources]) => ({
    datasetType: parameterDatasetType,
    creationTime: null,
    sources,
  }));

  return dataPerRow.map((rowData) => {
    const inDocumentSources = rowData.sources.filter((source) => source.sourceInfo != null);
    const tableSources = inDocumentSources.filter(
      // Already filtered out sources without source info above, but TypeScript doesn't know that
      (source) =>
        source.sourceInfo != null && source.sourceInfo.sourceType === ParameterDatasetSourceType.TABLE
    );
    const plateSources = inDocumentSources.filter(
      // Already filtered out sources without source info above, but TypeScript doesn't know that
      (source) =>
        source.sourceInfo != null && source.sourceInfo.sourceType === ParameterDatasetSourceType.PLATE
    );
    const scratchPlateSources = inDocumentSources.filter(
      (source) =>
        source.sourceInfo != null && source.sourceInfo.sourceType === ParameterDatasetSourceType.SCRATCH_PLATE
    );
    const renderedSourceKindCount = [tableSources, plateSources, scratchPlateSources].filter(
      (sources) => sources.length > 0
    ).length;

    return {
      id: rowData.datasetType,
      data: {
        dataset: (
          <DatasetCellContent
            datasetName={STATIC_DATASET_NAMES[rowData.datasetType as DatasetType]!}
            datasetId={rowData.datasetType}
          />
        ),
        creationTime: <CreatedAtCellContent createdAt={rowData.creationTime} />,
        dataSources: (
          <div>
            {tableSources.length > 0 && (
              <DataSourcesWithIcons
                dataSources={tableSources.map(({sourceInfo}) => ({
                  name: sourceInfo.sourceIdentifier,
                  stepName: sourceInfo.stepOrDocument.name,
                }))}
                iconName="table"
              />
            )}
            {plateSources.length > 0 && (
              <ChipList
                chipSize={ChipSize.MINI}
                isVertical={false}
                itemIds={[...new Set(plateSources.map(({sourceInfo}) => sourceInfo.sourceIdentifier))]}
                maxChips={2}
                showChipsAsLinks={false}
              />
            )}
            {scratchPlateSources.length > 0 && (
              <DataSourcesWithIcons
                dataSources={scratchPlateSources.map(({sourceInfo}) => ({
                  name: sourceInfo.sourceIdentifier,
                  stepName: sourceInfo.stepOrDocument.name,
                }))}
                iconName="plate"
              />
            )}
            {inDocumentSources.length === 0 && 'N/A'}
          </div>
        ),
        steps:
          inDocumentSources.length !== 0 ? (
            <StepsCellContent
              stepsOrDocuments={inDocumentSources.map(({sourceInfo}) => sourceInfo.stepOrDocument)}
            />
          ) : (
            <div>N/A</div>
          ),
        // If more than one kind of source is being rendered (as separate lines in the table row), increase the row height so it doesn't look squished
        height: renderedSourceKindCount > 1 ? TABLE_ROW_BODY_HEIGHT * renderedSourceKindCount : undefined,
      },
      extra: {datasetType: rowData.datasetType as DatasetType},
    };
  });
}

export function getLabAutoDatasetRows(
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>
): Array<AnalysisSourceRow> {
  const dataPerRow = availableSourcesPerStep.flatMap((stepSources) =>
    stepSources.analysisInputs.laRuns.flatMap((laRun) =>
      laRun.outputs.map((output) => ({
        datasetId: isLabAutoExecutionDatasetInfo(output) ? output.datasetId : undefined,
        schemaName: laRun.schemaName,
        outputFileConfigName: output.outputFileConfigName,
        outputFileConfigId: output.outputFileConfigId,
        creationTime: isLabAutoExecutionDatasetInfo(output) ? output.createdAt : null,
        stepOrDocument: getStepOrDocumentFromSource(stepSources),
      }))
    )
  );
  return dataPerRow.map((rowData) => ({
    id: rowData.datasetId ?? rowData.outputFileConfigId,
    data: {
      dataset:
        rowData.datasetId != null ? (
          <DatasetChip id={rowData.datasetId} size={ChipSize.MINI} isLink={false} />
        ) : (
          <DatasetCellContent
            datasetName={rowData.outputFileConfigName}
            datasetId={rowData.outputFileConfigId}
          />
        ),
      dataSources: (
        <DataSourcesWithIcons
          dataSources={[
            {
              name: `${rowData.schemaName}${
                rowData.outputFileConfigName != null ? ` (${rowData.outputFileConfigName})` : ''
              }`,
              stepName: rowData.stepOrDocument.name,
            },
          ]}
          iconName="assay_run"
        />
      ),
      creationTime: <CreatedAtCellContent createdAt={rowData.creationTime} />,
      steps: <StepsCellContent stepsOrDocuments={[rowData.stepOrDocument]} />,
    },
    extra: {datasetType: DatasetType.LAB_AUTO},
  }));
}
